import { Box, Paper, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSupabase } from "../auth/useSupabase";
import { useGame } from "./useGame";
import GameContent from "./GameContent";
import {
  LoadingState,
  BoardNotFoundError,
  GameNotFoundError,
} from "./ErrorStates";

// Gradient text style used throughout the app
export const gradientColor = `linear-gradient(to bottom, white 20%, #fbcfff 80%)`;

export const gradientTextSx = {
  background: gradientColor,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  color: "transparent",
};

const Player = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const { supabase } = useSupabase();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Use the custom hook to manage game state
  const {
    loading,
    game,
    gameState,
    rounds,
    questions,
    lifelines,
    bonusRounds,
    classicBonusAnswers,
    classicBonusTeamResponses,
    closestGuessQuestions,
    team1,
    team2,
    team1LogoUrl,
    team2LogoUrl,
  } = useGame(supabase, gameId);

  // Handle different loading and error states
  if (loading) {
    return <LoadingState />;
  }

  if (!gameState) {
    return <BoardNotFoundError navigate={navigate} />;
  }

  if (!game || !team1 || !team2) {
    return <GameNotFoundError />;
  }

  // Main game display
  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 32px)",
        height: "calc(100vh - 32px)",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.primary.dark,
        p: "16px",
        gap: "8px",
        overflow: "auto",
      }}
    >
      {/* Header with Game Title */}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 1 },
          borderRadius: 2,
          bgcolor: theme.palette.primary.main,
          color: "white",
          textAlign: "center",
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h3"}
          component="h1"
          fontWeight="bold"
          sx={{
            ...gradientTextSx,
          }}
        >
          The Dozen Trivia
        </Typography>
      </Paper>

      {/* Game content component - handles rounds, questions, and scoreboard */}
      <GameContent
        game={game}
        gameState={gameState}
        rounds={rounds}
        questions={questions}
        lifelines={lifelines}
        bonusRounds={bonusRounds}
        classicBonusAnswers={classicBonusAnswers}
        classicBonusTeamResponses={classicBonusTeamResponses}
        closestGuessQuestions={closestGuessQuestions}
        team1={team1}
        team2={team2}
        team1LogoUrl={team1LogoUrl}
        team2LogoUrl={team2LogoUrl}
      />
    </Box>
  );
};

export default Player;
